'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import {
  TranscriptionMessage,
  setTranscripts,
  mergeTranscripts,
  reconcilePersistedTranscripts,
} from '@/store/slices/transcriptionSlice';
import { RootState } from '@/store/store';
import {
  fromPersistedTranscript,
  toPersistedTranscript,
  transcriptSyncIdentity,
  type PersistedTranscriptInput,
  type PersistedTranscriptItem,
} from '@/lib/transcripts/persistence';
import {
  SingleFlightTranscriptDeltaWriter,
  registerTranscriptSync,
  type TranscriptDeltaResponse,
  type TranscriptFlushResult,
} from '@/lib/transcripts/singleFlightDeltaWriter';

/**
 * Transcript Real-Time Sync Hook
 *
 * Provides real-time backend sync for call transcripts.
 * - Debounced writes to prevent rate limiting (500ms)
 * - Hydrates from Supabase on mount
 * - Loads persisted transcripts from Supabase for reconnect/refresh recovery
 *
 * IMPORTANT: This replaces the archive-only save pattern where transcripts
 * were only saved to Supabase when the user clicked "Archive Call".
 * Now transcripts sync in real-time during the call through a backend route.
 */

const SYNC_DEBOUNCE_MS = 500;

interface UseTranscriptSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

export interface TranscriptSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
  revision: number;
}

export interface TranscriptSyncController extends TranscriptSyncState {
  flush: (options?: { force?: boolean; keepalive?: boolean }) => Promise<TranscriptFlushResult>;
}

type DbTranscriptItem = PersistedTranscriptInput;

function fromDbFormat(item: DbTranscriptItem, index = 0): TranscriptionMessage {
  return fromPersistedTranscript(item, index);
}

function transcriptMergeKeys(transcript: TranscriptionMessage, index: number): string[] {
  const participant = transcript.participantIdentity || transcript.name || 'unknown';
  return [
    transcript.syncId || transcriptSyncIdentity(transcript, index),
    transcript.segmentId ? `segment-id:${participant}:${transcript.segmentId}` : '',
    transcript.segmentKey ? `segment-key:${participant}:${transcript.segmentKey}` : '',
    `legacy:${participant}:${transcript.timestamp}:${transcript.message}`,
  ].filter(Boolean);
}

function shouldPreferLocalHydrationValue(
  server: TranscriptionMessage,
  local: TranscriptionMessage,
): boolean {
  if (server.isFinal && !local.isFinal) return false;
  if (local.isFinal && !server.isFinal) return true;

  const serverSeq = server.segmentSeq;
  const localSeq = local.segmentSeq;
  if (
    typeof serverSeq === 'number' && Number.isFinite(serverSeq) &&
    typeof localSeq === 'number' && Number.isFinite(localSeq)
  ) {
    return localSeq >= serverSeq;
  }

  // This is the current local value captured after the GET began. When no
  // comparable source sequence exists, retain it as desired state so full
  // field metadata corrections are not silently overwritten by hydration.
  return true;
}

function mergeHydratedTranscripts(
  serverTranscripts: readonly TranscriptionMessage[],
  localTranscripts: readonly TranscriptionMessage[],
): { merged: TranscriptionMessage[]; localDesiredCount: number } {
  const merged = serverTranscripts.map((transcript) => ({ ...transcript }));
  const indexByKey = new Map<string, number>();
  const indexTranscript = (transcript: TranscriptionMessage, index: number) => {
    for (const key of transcriptMergeKeys(transcript, index)) indexByKey.set(key, index);
  };
  merged.forEach(indexTranscript);

  let localDesiredCount = 0;
  localTranscripts.forEach((local, localIndex) => {
    const match = transcriptMergeKeys(local, localIndex)
      .map((key) => indexByKey.get(key))
      .find((index): index is number => index !== undefined);
    if (match === undefined) {
      const nextIndex = merged.length;
      merged.push({ ...local });
      indexTranscript(local, nextIndex);
      localDesiredCount += 1;
      return;
    }

    const server = merged[match];
    if (shouldPreferLocalHydrationValue(server, local)) {
      const desired = {
        ...local,
        // The server checkpoint owns immutable persistence identity even when
        // a legacy/local alias was used to find the same logical segment.
        syncId: server.syncId ?? local.syncId,
      };
      merged[match] = desired;
      indexTranscript(desired, match);
      localDesiredCount += 1;
    }
  });

  merged.sort((a, b) => a.timestamp - b.timestamp);
  return { merged, localDesiredCount };
}

function createWriterId(): string {
  const randomId = globalThis.crypto?.randomUUID?.();
  if (randomId) return randomId;

  // Compatibility fallback for older embedded browsers without randomUUID().
  // writer_id is diagnostic/idempotency metadata, not a security token.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function responseRevision(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : fallback;
}

/**
 * Hook to sync transcripts through the backend in real-time
 */
export function useTranscriptSync(options: UseTranscriptSyncOptions): TranscriptSyncController {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { isLoading: authLoading } = useAuth();

  const transcripts = useSelector((state: RootState) => state.transcription.transcripts);
  const activeCallSnapshotRef = useRef({
    callId,
    transcripts: transcripts as readonly TranscriptionMessage[],
    acceptsRedux: true,
    quarantinedBaseline: null as readonly TranscriptionMessage[] | null,
  });
  const priorCallSnapshotsRef = useRef(new Map<string, readonly TranscriptionMessage[]>());
  if (activeCallSnapshotRef.current.callId !== callId) {
    const previous = activeCallSnapshotRef.current;
    priorCallSnapshotsRef.current.set(previous.callId, previous.transcripts);
    // Quarantine the still-mounted Redux array until the new call's DB
    // checkpoint establishes which segments actually belong to it.
    activeCallSnapshotRef.current = {
      callId,
      transcripts: [],
      acceptsRedux: false,
      quarantinedBaseline: transcripts,
    };
  } else if (activeCallSnapshotRef.current.acceptsRedux) {
    activeCallSnapshotRef.current.transcripts = transcripts;
  } else if (transcripts !== activeCallSnapshotRef.current.quarantinedBaseline) {
    // The call page reset or a new stream produced a new Redux array. It is no
    // longer the exact quarantined call-A snapshot, so capture it for call B.
    activeCallSnapshotRef.current = {
      callId,
      transcripts,
      acceptsRedux: true,
      quarantinedBaseline: null,
    };
  }

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dbCallIdRef = useRef<string | null>(null);
  const writerRef = useRef<SingleFlightTranscriptDeltaWriter | null>(null);
  const generationRef = useRef(0);
  const hydrationReadyRef = useRef(false);
  const hydrationStartedRef = useRef(false);
  const persistedCacheRef = useRef(new WeakMap<TranscriptionMessage, PersistedTranscriptItem>());
  const lastQueuedObjectBySyncIdRef = useRef(new Map<string, TranscriptionMessage>());
  const stateRef = useReactiveRef<TranscriptSyncState>({
    isLoading: enabled && !!callId,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
    revision: 0,
  });

  const prepareSegments = useCallback((
    items: readonly TranscriptionMessage[],
    changedObjectsOnly = false,
  ) => {
    const prepared: PersistedTranscriptItem[] = [];
    items.forEach((item, index) => {
      const cached = persistedCacheRef.current.get(item);
      const persisted = cached ?? toPersistedTranscript(item, index);
      if (!cached) persistedCacheRef.current.set(item, persisted);
      if (
        changedObjectsOnly &&
        lastQueuedObjectBySyncIdRef.current.get(persisted.sync_id) === item
      ) {
        return;
      }
      lastQueuedObjectBySyncIdRef.current.set(persisted.sync_id, item);
      prepared.push(persisted);
    });
    return prepared;
  }, []);

  const enqueueCurrent = useCallback((
    defer = false,
    includeUnchanged = false,
  ) => {
    const writer = writerRef.current;
    const current = activeCallSnapshotRef.current;
    if (!writer || !enabled || current.callId !== callId || current.transcripts.length === 0) {
      return;
    }
    writer.enqueue(prepareSegments(current.transcripts, !includeUnchanged), { defer });
  }, [callId, enabled, prepareSegments]);

  const flush = useCallback(async (
    flushOptions: { force?: boolean; keepalive?: boolean } = {},
  ): Promise<TranscriptFlushResult> => {
    const writer = writerRef.current;
    if (!enabled || !callId || !writer) {
      return {
        ok: true,
        revision: stateRef.current.revision,
        writerSequence: 0,
        pendingSegments: 0,
      };
    }
    enqueueCurrent(true);
    return writer.flush(flushOptions);
  }, [callId, enabled, enqueueCurrent]);

  // Create one writer per call generation. Late old-call responses cannot
  // update the current hook state because every callback is generation-gated.
  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    hydrationReadyRef.current = false;
    hydrationStartedRef.current = false;
    persistedCacheRef.current = new WeakMap();
    lastQueuedObjectBySyncIdRef.current = new Map();
    dbCallIdRef.current = null;
    stateRef.current = {
      isLoading: enabled && !!callId,
      isSyncing: false,
      lastSyncTime: null,
      error: null,
      dbCallId: null,
      revision: 0,
    };

    if (!enabled || !callId) {
      writerRef.current = null;
      return;
    }

    const writer = new SingleFlightTranscriptDeltaWriter({
      callId,
      writerId: createWriterId(),
      requestTimeoutMs: 30_000,
      send: async (request, sendOptions): Promise<TranscriptDeltaResponse> => {
        const startedAt = Date.now();
        const body = JSON.stringify(request);
        const response = await fetch('/api/transcripts/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          signal: sendOptions.signal,
          keepalive: sendOptions.keepalive,
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Transcript sync failed: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        const dbCallId = typeof result?.call_id === 'string' ? result.call_id : null;
        if (dbCallId && generationRef.current === generation) {
          dbCallIdRef.current = dbCallId;
        }
        logger.debug('useTranscriptSync', 'Synced transcript delta', {
          callId,
          sequence: request.writer_sequence,
          count: request.content.length,
          bytes: new TextEncoder().encode(body).byteLength,
          durationMs: Date.now() - startedAt,
          stale: result?.stale === true,
          keepalive: sendOptions.keepalive,
        });
        return {
          ...result,
          revision: responseRevision(result?.revision, request.base_revision),
          stale: result?.stale === true,
          transcripts: Array.isArray(result?.transcripts) ? result.transcripts : undefined,
        };
      },
      onStateChange: (writerState) => {
        if (generationRef.current !== generation) return;
        stateRef.current = {
          ...stateRef.current,
          isSyncing: writerState.isSyncing,
          lastSyncTime: writerState.lastSyncTime,
          error: writerState.lastError,
          dbCallId: dbCallIdRef.current,
          revision: writerState.revision,
        };
      },
      onAuthoritativeSegments: (segments) => {
        if (generationRef.current !== generation) return;
        dispatch(reconcilePersistedTranscripts(
          segments.map((segment, index) => fromPersistedTranscript(segment, index)),
        ));
      },
    });
    writerRef.current = writer;
    const unregister = registerTranscriptSync(callId, async (options) => {
      if (generationRef.current !== generation) {
        return {
          ok: false,
          revision: writer.getState().revision,
          writerSequence: 0,
          pendingSegments: writer.getState().pendingSegments,
          error: new Error('Transcript writer generation is stale'),
        };
      }
      enqueueCurrent(true);
      return writer.flush(options);
    });

    const handlePageHide = () => {
      if (generationRef.current !== generation) return;
      enqueueCurrent(true, true);
      void writer.flush({ keepalive: true });
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      generationRef.current += 1;
      unregister();
      window.removeEventListener('pagehide', handlePageHide);
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }

      const active = activeCallSnapshotRef.current;
      const finalTranscripts = active.callId === callId
        ? active.transcripts
        : priorCallSnapshotsRef.current.get(callId) ?? [];
      priorCallSnapshotsRef.current.delete(callId);
      if (finalTranscripts.length > 0) {
        writer.enqueue(prepareSegments(finalTranscripts), { defer: true });
        void writer.flush({ keepalive: true }).finally(() => writer.dispose());
      } else {
        writer.dispose();
      }
      if (writerRef.current === writer) writerRef.current = null;
    };
  }, [callId, dispatch, enabled, enqueueCurrent, prepareSegments]);

  // Hydration is the server checkpoint gate. Only DB content seeds the acked
  // map; locally arrived segments are merged and then queued as real deltas.
  useEffect(() => {
    if (!enabled || !callId || authLoading) return;
    if (hydrationReadyRef.current || hydrationStartedRef.current) return;
    const generation = generationRef.current;
    const writer = writerRef.current;
    if (!writer) return;

    let cancelled = false;
    let completed = false;
    hydrationStartedRef.current = true;
    const load = async () => {
      let content: DbTranscriptItem[] = [];
      let revision = 0;
      let loadError: Error | null = null;
      let dbCallId: string | null = null;
      try {
        const response = await fetch(`/api/transcripts/${encodeURIComponent(callId)}`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Transcript load failed: ${response.status} - ${errorText}`);
        }
        const result = await response.json();
        dbCallId = typeof result?.call_id === 'string' ? result.call_id : null;
        content = Array.isArray(result?.transcripts) ? result.transcripts : [];
        revision = responseRevision(result?.revision);
      } catch (error) {
        loadError = error instanceof Error ? error : new Error(String(error));
        logger.error('useTranscriptSync', 'Failed to load transcript checkpoint', {
          callId,
          error: loadError,
        });
      }

      if (cancelled || generationRef.current !== generation) return;
      dbCallIdRef.current = dbCallId;
      writer.seedCheckpoint(revision, content);

      const dbMessages = content.map(fromDbFormat);
      const active = activeCallSnapshotRef.current;
      const localTranscripts = active.callId === callId ? active.transcripts : [];
      const { merged, localDesiredCount } = mergeHydratedTranscripts(
        dbMessages,
        localTranscripts,
      );
      if (activeCallSnapshotRef.current.callId === callId) {
        activeCallSnapshotRef.current = {
          callId,
          transcripts: merged,
          acceptsRedux: true,
          quarantinedBaseline: null,
        };
      }
      // Dispatch even when empty: on a call switch this is the ownership
      // handoff that removes the previous call's Redux transcript.
      dispatch(setTranscripts(merged));
      if (merged.length > 0) writer.enqueue(prepareSegments(merged));

      hydrationReadyRef.current = true;
      completed = true;
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: loadError,
        dbCallId,
        revision: writer.getState().revision,
      };
      logger.info('useTranscriptSync', 'Loaded transcript checkpoint', {
        callId,
        revision,
        fromDb: dbMessages.length,
        localDesired: localDesiredCount,
      });
    };
    void load();
    return () => {
      cancelled = true;
      if (!completed && generationRef.current === generation) {
        hydrationStartedRef.current = false;
      }
    };
  }, [authLoading, callId, dispatch, enabled, prepareSegments]);

  useEffect(() => {
    if (!enabled || !callId || !hydrationReadyRef.current || transcripts.length === 0) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      enqueueCurrent();
      syncTimeoutRef.current = null;
    }, debounceMs);
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [callId, debounceMs, enabled, enqueueCurrent, transcripts]);

  return { ...stateRef.current, flush };
}

/**
 * Hook to hydrate transcription state from storage.
 * Call once on Conference mount to restore previous session, AND on
 * RoomEvent.Reconnected with `{ force: true, merge: true }` to backfill
 * any segments missed during the disconnect window.
 *
 * Per PERF_SWEEP §B Codex Risk #2 (2026-05-28): the original hook used
 * `hydratedRef.current` as a one-shot guard, so the second/Nth call (e.g.
 * from a reconnect) was a no-op — and any segments the server persisted
 * during the gap stayed silently missing in Redux.
 */
export function useTranscriptHydration(callId: string): {
  hydrate: (opts?: { force?: boolean; merge?: boolean }) => Promise<void>;
  isHydrated: boolean;
} {
  const dispatch = useDispatch();
  const hydratedRef = useRef(false);
  const currentTranscripts = useSelector(
    (state: RootState) => state.transcription.transcripts,
  );
  const currentTranscriptsRef = useRef(currentTranscripts);
  currentTranscriptsRef.current = currentTranscripts;

  const hydrate = useCallback(
    async (opts?: { force?: boolean; merge?: boolean }) => {
      // Default behavior is one-shot and additive when live Redux data exists.
      // Reconnect callers pass { force: true, merge: true } to bypass the
      // one-shot guard explicitly.
      if (!callId) return;
      if (!opts?.force && hydratedRef.current) return;

      try {
        const response = await fetch(`/api/transcripts/${encodeURIComponent(callId)}`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Transcript hydrate failed: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        if (Array.isArray(result?.transcripts) && result.transcripts.length > 0) {
          const transcripts = (result.transcripts as DbTranscriptItem[]).map(fromDbFormat);
          if (opts?.merge || currentTranscriptsRef.current.length > 0) {
            // mergeTranscripts dedupes by segmentKey (or timestamp:message
            // fallback) and re-sorts by timestamp — safe to call on top
            // of live state without clobbering operator scroll position
            // or in-flight finalizations.
            dispatch(mergeTranscripts(transcripts));
            logger.info('useTranscriptHydration', 'Re-hydrated (merge) from backend transcripts route', {
              count: transcripts.length,
              forced: !!opts?.force,
            });
          } else {
            dispatch(setTranscripts(transcripts));
            logger.info('useTranscriptHydration', 'Hydrated from backend transcripts route', { count: transcripts.length });
          }
        }

        hydratedRef.current = true;
      } catch (err: unknown) {
        logger.error('useTranscriptHydration', 'Hydration failed', { error: err, forced: !!opts?.force });
      }
    },
    [callId, dispatch],
  );

  return {
    hydrate,
    isHydrated: hydratedRef.current,
  };
}

export default useTranscriptSync;
